import * as XLSX from "xlsx";

/**
 * Securely read an Excel file with validation and size limits
 * @param {File} file - The Excel file to read
 * @param {Object} options - Options for processing
 * @param {number} options.maxFileSize - Maximum file size in bytes (default: 10MB)
 * @param {string[]} options.allowedExtensions - Allowed file extensions (default: ['.xlsx', '.xls', '.csv'])
 * @param {number} options.maxSheets - Maximum number of sheets to process (default: 10)
 * @param {number} options.maxRows - Maximum number of rows to process per sheet (default: 10000)
 * @returns {Promise} - Promise resolving to the processed data
 */
export const secureReadExcelFile = (file, options = {}) => {
  return new Promise((resolve, reject) => {
    // Default options
    const {
      maxFileSize = 1 * 1024 * 1024 * 1024, // Default: 1GB
      allowedExtensions = [".xlsx", ".xls", ".csv"],
      maxSheets = 10,
      maxRows = 10000,
    } = options;

    // Validate file existence
    if (!file) {
      reject(new Error("No file provided"));
      return;
    }

    // Validate file size
    if (file.size > maxFileSize) {
      const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(2);
      reject(
        new Error(
          `File size exceeds the maximum allowed size of ${maxSizeMB}MB`
        )
      );
      return;
    }

    // Validate file extension
    const fileName = file.name || "";
    const fileExtension = "." + fileName.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      reject(
        new Error(
          `Invalid file type. Allowed types: ${allowedExtensions.join(", ")}`
        )
      );
      return;
    }

    // Read the file
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // Validate number of sheets
        const sheetNames = workbook.SheetNames;
        if (sheetNames.length > maxSheets) {
          reject(
            new Error(
              `File contains too many sheets. Maximum allowed: ${maxSheets}`
            )
          );
          return;
        }

        // Process each sheet with row limits
        const result = {};

        for (const sheetName of sheetNames) {
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

          // Validate number of rows
          if (jsonData.length > maxRows) {
            reject(
              new Error(
                `Sheet "${sheetName}" contains too many rows. Maximum allowed: ${maxRows}`
              )
            );
            return;
          }

          // Sanitize data (remove potentially dangerous content)
          const sanitizedData = jsonData.map((row) => {
            const sanitizedRow = {};
            Object.keys(row).forEach((key) => {
              const value = String(row[key]);
              sanitizedRow[key] = value.replace(
                /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
                ""
              );
            });
            return sanitizedRow;
          });

          result[sheetName] = sanitizedData;
        }

        resolve(result);
      } catch (error) {
        reject(new Error(`Error processing Excel file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error("Error reading the file"));
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Securely export data to Excel file with validation
 * @param {Array|Object} data - The data to export
 * @param {string} fileName - The name of the file to export
 * @param {Object} options - Options for exporting
 * @returns {void} - Triggers a file download
 */
export const secureExportToExcel = (
  data,
  fileName = "export.xlsx",
  options = {}
) => {
  try {
    if (!data || (Array.isArray(data) && data.length === 0)) {
      console.error("No data provided for export");
      return;
    }

    const workbook = XLSX.utils.book_new();

    if (Array.isArray(data)) {
      const worksheet = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    } else if (typeof data === "object") {
      Object.keys(data).forEach((sheetName) => {
        if (Array.isArray(data[sheetName]) && data[sheetName].length > 0) {
          const worksheet = XLSX.utils.json_to_sheet(data[sheetName]);
          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        }
      });
    } else {
      console.error("Invalid data format for export");
      return;
    }

    XLSX.writeFile(workbook, fileName);
  } catch (error) {
    console.error("Error exporting to Excel:", error.message);
  }
};

export default {
  secureReadExcelFile,
  secureExportToExcel,
};
